// src/App.js - FINAL DYNAMIC VERSION (With Persistent NPS Dismissal)

import React, { useState, useEffect, lazy, Suspense } from 'react';
// ✅ NEW: Import functions to update the user's document in Firestore
import { collection, doc, onSnapshot, query, where, updateDoc, arrayUnion } from 'firebase/firestore'; 
import { db } from './firebase';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Routes, Route, Navigate, Outlet, Link, useLocation } from 'react-router-dom';

// --- Screen & Component Imports ---
import LandingPage from './screens/LandingPage';
import LoginScreen from './screens/LoginScreen';
import SignUpScreen from './screens/SignUpScreen';
import CompleteSignupScreen from './screens/CompleteSignupScreen';
import HomeScreen from './screens/HomeScreen';
import MarketplaceScreen from './screens/MarketplaceScreen';
import ItemDetailScreen from './screens/ItemDetailScreen';
import ItemDetailScreenPublic from './screens/ItemDetailScreenPublic';
import LoadingScreen from './screens/LoadingScreen';
import TradeListScreen from './screens/TradeListScreen';
import Header from './components/Header';
import NavBar from './components/NavBar';
import ScrollToTop from './components/ScrollToTop';
import Modal from './components/Modal';
import Footer from './components/Footer';
import NPSModal from './components/NPSModal';

// Screens with heavy libraries (admin: xlsx/recharts/jsPDF; map: leaflet;
// photos: image compression; terms: markdown) load on demand.
const AddItemScreen = lazy(() => import('./screens/AddItemScreen'));
const ProfileScreen = lazy(() => import('./screens/ProfileScreen'));
const AdminScreen = lazy(() => import('./screens/admin/AdminScreen'));
const TradeScreen = lazy(() => import('./screens/TradeScreen'));
const ChatScreen = lazy(() => import('./screens/ChatScreen'));
const EditItemScreen = lazy(() => import('./screens/EditItemScreen'));
const TermsScreen = lazy(() => import('./screens/TermsScreen'));
const PrivacyPolicyScreen = lazy(() => import('./screens/PrivacyPolicyScreen'));
const DeleteAccountScreen = lazy(() => import('./screens/DeleteAccountScreen'));
const ChildSafetyScreen = lazy(() => import('./screens/ChildSafetyScreen'));

// --- Layout Components ---
const AuthenticatedLayout = ({ partners, partnersConfig, showNPSModal, activeCampaignId, profile, onNPSDismiss, onNPSSubmit }) => {
    const { modal, setModal, signOut: onLogout } = useAuth();
    return (
        <div className="font-sans flex flex-col min-h-screen bg-gray-50 text-gray-800">
            <Header onLogout={onLogout} />
            <main className="flex-grow container mx-auto px-4 py-8">
                <Suspense fallback={<LoadingScreen />}><Outlet /></Suspense>
            </main>
            {modal && <Modal modal={modal} setModal={setModal} />}
            {profile?.role !== 'admin' && showNPSModal && activeCampaignId && (
                <NPSModal 
                    campaignId={activeCampaignId}
                    onDismiss={onNPSDismiss}
                    onSubmit={onNPSSubmit}
                />
            )}
            <NavBar onLogout={onLogout} />
            <Footer partners={partners} partnersConfig={partnersConfig} />
        </div>
    );
};

// Renders the global modal while the user is completing signup (no layout).
const NeedsProfileModal = () => {
    const { modal, setModal } = useAuth();
    return modal ? <Modal modal={modal} setModal={setModal} /> : null;
};

const PublicLayout = ({ partners, partnersConfig }) => { 
    
    const { modal, setModal } = useAuth();
    return (
        <div className="font-sans flex flex-col min-h-screen bg-gray-50 text-gray-800">
            <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="text-2xl font-bold text-green-700">Escambo Digital</Link>
                    <Link to="/login" className="bg-green-600 text-white px-5 py-2 rounded-lg shadow hover:bg-green-700 transition">Entrar</Link>
                </div>
            </header>
            <main className="flex-grow">
                <Suspense fallback={<LoadingScreen />}><Outlet /></Suspense>
            </main>
            {modal && <Modal modal={modal} setModal={setModal} />}
            <Footer partners={partners} partnersConfig={partnersConfig} />
        </div>
    );

};

// --- Main App Logic ---
function AppContent() {
    const { user, profile, loading, validationStatus, setModal } = useAuth();
    const { pathname } = useLocation();
  
    // State for partners
    const [partners, setPartners] = useState([]);
    const [partnersConfig, setPartnersConfig] = useState({ show: false });

    // State for NPS feature
    const [npsSettings, setNpsSettings] = useState({ show: false });
    const [activeCampaignId, setActiveCampaignId] = useState(null);
    const [hasDismissedInSession, setHasDismissedInSession] = useState(false);

    useEffect(() => {
        const partnersUnsubscribe = onSnapshot(collection(db, 'partners'), (snapshot) => setPartners(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
        const partnerConfigUnsubscribe = onSnapshot(doc(db, 'app_settings', 'partnerSectionConfig'), (doc) => setPartnersConfig(doc.exists() ? doc.data() : { show: false }));
        const npsSettingsUnsubscribe = onSnapshot(doc(db, 'app_settings', 'nps_modal_settings'), (doc) => setNpsSettings(doc.exists() ? doc.data() : { show: false }));
        const activeCampaignQuery = query(collection(db, 'nps_campaigns'), where("isActive", "==", true));
        const activeCampaignUnsubscribe = onSnapshot(activeCampaignQuery, (snapshot) => {
            if (!snapshot.empty) {
                setActiveCampaignId(snapshot.docs[0].id);
            } else {
                setActiveCampaignId(null);
            }
        });
        return () => {
            partnersUnsubscribe();
            partnerConfigUnsubscribe();
            npsSettingsUnsubscribe();
            activeCampaignUnsubscribe();
        };
    }, []);

    // ✅ NEW: Async function to update the user's profile in Firestore
    const handleNPSInteraction = async (campaignId) => {
        setHasDismissedInSession(true); // Hide immediately for this session
        if (user && campaignId) {
            const userRef = doc(db, 'users', user.uid);
            try {
                // Atomically add the campaign ID to a new array field in the user's document
                await updateDoc(userRef, {
                    interactedNPSCampaigns: arrayUnion(campaignId)
                });
            } catch (error) {
                console.error("Failed to update user NPS interaction history:", error);
            }
        }
    };

    const handleNPSSubmit = (score, campaignId) => {
        console.log(`User submitted score: ${score} for campaign: ${campaignId}`);
        // You would also save the score to the campaign's subcollection here
        handleNPSInteraction(campaignId);
    };

    const handleNPSDismiss = () => {
        if (activeCampaignId) {
            handleNPSInteraction(activeCampaignId);
        }
    };

    if (loading || (user && validationStatus === 'pending')) {
        // The landing page is public and pre-rendered (landing.html): keep
        // showing it while auth initializes instead of a loading screen. A
        // signed-in user is sent to /home once auth confirms them.
        if (pathname === '/' && !user) {
            return (
                <Routes>
                    <Route element={<PublicLayout partners={partners} partnersConfig={partnersConfig} />}>
                        <Route path="/" element={<LandingPage />} />
                    </Route>
                </Routes>
            );
        }
        return <LoadingScreen />;
    }

    const isAuthenticated = user && profile && validationStatus === 'success';

    // Signed in (e.g. first Google login) but no profile yet: only the
    // completion screen and the documents it links to are reachable.
    if (user && validationStatus === 'needs_profile') {
        return (
            <>
                <Suspense fallback={<LoadingScreen />}>
                <Routes>
                    <Route path="/complete-signup" element={<CompleteSignupScreen />} />
                    <Route path="/terms" element={<TermsScreen />} />
                    <Route path="/privacy" element={<PrivacyPolicyScreen />} />
                    <Route path="/excluir-conta" element={<DeleteAccountScreen />} />
                    <Route path="/seguranca-infantil" element={<ChildSafetyScreen />} />
                    <Route path="*" element={<Navigate to="/complete-signup" replace />} />
                </Routes>
                </Suspense>
                <NeedsProfileModal />
            </>
        );
    }
    
    // ✅ NEW: The logic to show the modal now checks the user's profile from the database
    const userHasSeenThisCampaign = profile?.interactedNPSCampaigns?.includes(activeCampaignId);
    const showNPSModal = npsSettings.show && activeCampaignId && !userHasSeenThisCampaign && !hasDismissedInSession;

    return (
        <Routes>
            {isAuthenticated ? (
                <>
                {/* Painel Admin has its own full-screen layout. */}
                {profile.role === 'admin' && (
                    <Route path="/admin/*" element={<Suspense fallback={<LoadingScreen />}><AdminScreen /></Suspense>} />
                )}
                <Route 
                    element={
                        <AuthenticatedLayout 
                            partners={partners} 
                            partnersConfig={partnersConfig}
                            showNPSModal={showNPSModal}
                            activeCampaignId={activeCampaignId}
                            profile={profile}
                            onNPSDismiss={handleNPSDismiss}
                            onNPSSubmit={handleNPSSubmit}
                        />
                    }
                >
                    <Route path="/home" element={<HomeScreen />} />
                    <Route path="/market" element={<MarketplaceScreen setModal={setModal}/>} />
                    <Route path="/market/:itemId" element={<ItemDetailScreen setModal={setModal}/>} />
                    <Route path="/profile" element={<ProfileScreen setModal={setModal} />} />
                    <Route path="/trades" element={<TradeListScreen setModal={setModal}/>} />
                    <Route path="/chat/:chatId" element={<ChatScreen />} />
                    <Route path="/trades/:tradeId" element={<TradeScreen setModal={setModal}/>} />
                    <Route path="/add-item" element={<AddItemScreen />} />
                    <Route path="/edit-item/:itemId" element={<EditItemScreen setModal={setModal} />} />
                    <Route path="/terms" element={<TermsScreen />} />
                    <Route path="/privacy" element={<PrivacyPolicyScreen />} />
                    <Route path="/excluir-conta" element={<DeleteAccountScreen />} />
                    <Route path="/seguranca-infantil" element={<ChildSafetyScreen />} />
                    <Route path="*" element={<Navigate to="/home" replace />} />
                </Route>
                </>
            ) : (
                <Route element={<PublicLayout partners={partners} partnersConfig={partnersConfig} />}>
                   <Route path="/" element={<LandingPage />} />
                   <Route path="/login" element={<LoginScreen />} />
                   <Route path="/signup" element={<SignUpScreen />} />
                   <Route path="/terms" element={<TermsScreen />} />
                   <Route path="/privacy" element={<PrivacyPolicyScreen />} />
                   <Route path="/excluir-conta" element={<DeleteAccountScreen />} />
                   <Route path="/seguranca-infantil" element={<ChildSafetyScreen />} />
		   <Route path="/market/:itemId" element={<ItemDetailScreenPublic setModal={setModal}/>} /> 
                   <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
            )}
        </Routes>
    );
}

// --- App Wrapper ---
export default function App() {
    return (
        <AuthProvider>
            <ScrollToTop />
            <AppContent />
        </AuthProvider>
    );
}
